import MasterCardIcon from '@/components/customercenter-ui/icons/masterCard';
import SuccessIcon from '@/components/customercenter-ui/icons/success';
import VisaIcon from '@/components/customercenter-ui/icons/visa';
import { Button } from '@/components/customercenter-ui/ui/button';
import { getDivisionSvgs } from '@/components/customercenter-ui/utils/crediCardUtils';
import {
  formatExpirationYear,
  maskPhoneNumber,
  maskPostalCode,
} from '@/components/customercenter-ui/utils/formatUtils';
import { PaymentMethods, useCheckout } from '@/context/checkoutProvider';
import AmericanExpress from '@/customercenter-ui/icons/americanExpress';
import Discover from '@/customercenter-ui/icons/discover';
import { GlobalPageTexts } from '@/helpers/i18n';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { formatCurrency, TranslateText } from '@/helpers/utils/siteUtils';
import PreQualificationAccordion from './PreQualificationAccordion';
import { DEFAULT_SITE_LANG } from '../../../../../constants/constant';

interface PaymentProps {
  dataSource: any;
  color: string;
  edit: boolean;
}

const ColorPayment: React.FC<PaymentProps> = ({ edit, color }) => {
  const {
    contactInformation,
    goToCheckoutStep,
    selectedPaymentMethod,
    cardDetails,
    cashDetails,
    checkDetails,
    soaCustomer,
    contextCart: data,
  }: any = useCheckout();
  const Texts = TranslateText(GlobalPageTexts, DEFAULT_SITE_LANG);

  const customerData = soaCustomer && soaCustomer?.DivisionCustomers ? soaCustomer?.DivisionCustomers?.[0] : null;

  const divisionCode = customerData?.divisionCode ?? 'ZB';
  const CreditAccount = customerData?.CreditAccount;

  const cardIcon = (creditCardType: string) => {
    switch (creditCardType?.toLowerCase()) {
      case 'visa':
        return <VisaIcon className="h-[78px] w-[122px]" />;
      case 'mastercard':
        return <MasterCardIcon className="h-[78px] w-[122px]" />;
      case 'discover':
        return <Discover className="h-[78px] w-[122px]" />;
      case 'amex':
        return <AmericanExpress className="h-[78px] w-[122px]" />;
      default:
        return null;
    }
  };

  const getPaymentData = () => {
    switch (selectedPaymentMethod) {
      case PaymentMethods.CARD:
        return (
          <>
            <section className="gbomit pb-6 pt-4 lg:pb-5">{cardIcon(cardDetails?.creditCardType)}</section>
            <section className="mb-4">
              <section className="pb-2 font-bold">{mapLocaleToMeaningfulFormat().cardNumber}</section>
              <section className="gbmask">{`**** ${cardDetails?.creditCardBin}`}</section>
            </section>
            <section className="mb-4">
              <section className="pb-2 font-bold">{mapLocaleToMeaningfulFormat().expiryDate}</section>
              <section className="gbmask pb-2">
                {`${mapLocaleToMeaningfulFormat().expire} ${cardDetails?.expirationMonth}/${formatExpirationYear(
                  cardDetails?.expirationYear,
                )}`}
              </section>
            </section>
          </>
        );
      case PaymentMethods.CREDIT:
        return (
          <>
            <section className="pb-14 pt-4 lg:pb-9">
              <section className="gbomit">{getDivisionSvgs(divisionCode, '')}</section>
              {CreditAccount && (
                <section className="mb-4 mt-5">
                  <section className="pb-2 font-bold">{mapLocaleToMeaningfulFormat().accountNo}</section>
                  <section className="gbmask pb-2">{CreditAccount?.creditAccountId}</section>
                  <section className="pb-2 font-bold">{mapLocaleToMeaningfulFormat().availableCredit}</section>
                  <section className="pb-2">
                    {formatCurrency(CreditAccount?.AccountInfo?.availableCredit?.toString())}
                  </section>
                </section>
              )}
            </section>
          </>
        );
      case PaymentMethods.CASH:
        return (
          <>
            <section className="pb-14 pt-4 lg:pb-9">{mapLocaleToMeaningfulFormat().Cash}</section>
            <section className="pb-3 font-bold">{mapLocaleToMeaningfulFormat().batchNumber}</section>
            <section className="">{cashDetails?.batchNumber}</section>
            <section className="pb-3 font-bold">{mapLocaleToMeaningfulFormat().cashAmount}</section>
            <section className="">{cashDetails?.cashAmount}</section>
          </>
        );
      case PaymentMethods.CHECK:
        return (
          <>
            <section className="pb-14 pt-4 lg:pb-9">{mapLocaleToMeaningfulFormat().Check}</section>
            <section className="pb-3 font-bold">{mapLocaleToMeaningfulFormat().batchNumber}</section>
            <section className="">{checkDetails?.batchNumber}</section>
            <section className="pb-3 font-bold">{mapLocaleToMeaningfulFormat().checkNumber}</section>
            <section className="">{checkDetails?.checkNumber}</section>
            <section className="pb-3 font-bold">{mapLocaleToMeaningfulFormat().totalCheckAmount}</section>
            <section className="">{checkDetails?.totalCheckAmount}</section>
          </>
        );
      case PaymentMethods.NO_CHARGE:
        return <section className="pb-14 pt-4 lg:pb-9">{mapLocaleToMeaningfulFormat().noChargeTitle}</section>;
      default:
        return <div>{mapLocaleToMeaningfulFormat().invalidPayment}</div>;
    }
  };

  return (
    <section className="border-t border-foggyGray pl-4 pr-5 lg:rounded-md lg:border lg:px-26 lg:shadow-md lg:shadow-foggyGray">
      <section className="flex items-start gap-x-3 lg:gap-x-3">
        <section className="pt-6 lg:pt-6">
          <SuccessIcon className={`fill-[ h-9 w-9${color}]`} />
        </section>
        <article className="mr-auto">
          <h1 className="pt-7 text-[22px] font-bold leading-7 text-forestGreen">{Texts.payment}</h1>
        </article>
        {edit ? (
          <Button
            className="bg-white pt-8 text-[16px] font-normal leading-5  text-blueLight underline"
            onClick={() => {
              return goToCheckoutStep(2);
            }}
          >
            Edit
          </Button>
        ) : null}
      </section>
      <section className="grid py-6 lg:grid-cols-2">
        <article className="pl-11">
          <strong className="pt-7 text-[18px] font-bold leading-6">
            {mapLocaleToMeaningfulFormat().billingAddress}
          </strong>
          <section className="gbmask overflow-hidden text-ellipsis pr-24 pt-2.5 text-base font-normal lg:pr-3">
            <p>
              {data?.billingAddress?.firstName} {data?.billingAddress?.lastName}
            </p>
            <p>{data?.billingAddress?.streetName}</p>
            <p>{data?.billingAddress?.additionalAddressInfo}</p>
            <p>
              {data?.billingAddress?.city}, {data?.billingAddress?.state}{' '}
              {maskPostalCode(data?.billingAddress?.postalCode ?? '')}
            </p>
            <p className="mt-3">{maskPhoneNumber(data?.billingAddress?.phone ?? '')}</p>
            <p className="pt-6 text-[18px] font-bold">{mapLocaleToMeaningfulFormat().contactInfo}</p>
            <p className="gbmask break-words md:w-48 lg:w-auto">{contactInformation?.email}</p>
          </section>
        </article>

        <section className="pb-8 pl-12 lg:pb-9">
          <strong className="pt-8 text-[18px] font-bold leading-6  lg:pt-7">
            {mapLocaleToMeaningfulFormat().paymentMethod}
          </strong>
          {getPaymentData()}
        </section>
      </section>
      {PaymentMethods?.CREDIT == selectedPaymentMethod && <PreQualificationAccordion />}
    </section>
  );
};

export default ColorPayment;
